import countryList from "react-select-country-list";
import { DOCUMENT_CONFIGS } from "../models/superadmin";
import { TUploadedDocumentsData } from "../store/onboarding/types";

export const validDateDocTypes = ["teachers_registration", "working_with_children_check", "australian_work_visa"];
export const issueDateDocTypes = ["first_aid_certificate", "mandatory_reporting_training","permanent_residency_visa", "food_handler_certificate", "police_check", "right_to_work", "reference_check", "reference_check_1", "permanent_residency_visa"];
export const completionDateDocTypes = ["post_graduate_diploma", "bachelor_or_degree", "diploma", "certificate_iii", "cooking_qualification"];

export const isIssuedType = (type: string): boolean => issueDateDocTypes.includes(type);

export const getValidityYearsByType = (type: string): number => {
  const typeYearMap: Record<string, number> = {
    first_aid_certificate: 3,
    mandatory_reporting_training: 1,
    food_handler_certificate: 1,
    police_check: 1
  };

  return typeYearMap[type] || 1; // default to 1 year if not listed
};

export const getValidityMonths = (type: string): number => {
  const getYear = getValidityYearsByType(type);

  if(getYear > 0) {
    return getYear*12;
  }

  return 12;
}

export const getDocumentName = (type: string): string => {
  const docTypeName = DOCUMENT_CONFIGS.find( (docList) => docList.type === type);
  return docTypeName ? docTypeName.title : type.replace(/_/g, " ");
}

export const getDocumentDate = (doc: TUploadedDocumentsData): string => {
  const { document_type, completion_date, issued_date, validity_date } = doc;

  if (
    completionDateDocTypes.includes(document_type)
  ) {
    return completion_date;
  }

  if (
    issueDateDocTypes.includes(document_type)
  ) {
    return issued_date;
  }

  return validity_date;
};

export const getDateLabel = (type: string): string => {
  if (completionDateDocTypes.includes(type)) return "Completion date";
  if (issueDateDocTypes.includes(type)) return "Issue date";
  return "Valid until";
};

export const getDateAfterYears = (issueDateStr: string, yearsToAdd: number): string | null => {
  if (!issueDateStr) return null;

  const issueDate = new Date(issueDateStr);
  if (isNaN(issueDate.getTime())) return null;

  const year = issueDate.getFullYear() + yearsToAdd;
  const month = issueDate.getMonth(); // 0-indexed
  const day = issueDate.getDate();

  // Create new date with same month/day, but adjusted year
  const newDate = new Date(year, month, day);

  // If the new date's month is different, it means the original day didn't exist (e.g., Feb 29 → Mar 1)
  if (newDate.getMonth() !== month) {
    // Adjust to last valid day of the original month
    newDate.setDate(0); // sets to last day of previous month
  }

  const formatted = newDate.toISOString().slice(0, 10);
  return formatted;
};

const formatDateWithOptions = (dateStr: string, options: Intl.DateTimeFormatOptions): string | null => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-GB", options);
};

export const formatDateToReadable = (dateStr: string): string | null =>
  formatDateWithOptions(dateStr, { day: "2-digit", month: "short", year: "numeric" });

export const formatDateToReadableWithDay = (dateStr: string): string | null => {
  const result = formatDateWithOptions(dateStr, { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  return result ? result.replace(",", "") : null;
};

export const isOneMonthBefore = (issueDateStr: string): boolean => {
  if (!issueDateStr) return false;

  const issueDate = new Date(issueDateStr);
  if (isNaN(issueDate.getTime())) return false;

  const today = new Date();
  const oneMonthBefore = new Date(issueDate);
  oneMonthBefore.setMonth(issueDate.getMonth() - 1);

  // Return true only if today is within the 1-month window before issueDate
  return (today >= oneMonthBefore && today <= issueDate) || today > issueDate;
}

/**
 * Get the minimum date allowed for the document type
 * @returns Date | undefined - Minimum date or undefined if no restriction
 */
export const getMinDateByDocumentType = (documentType: string): Date | undefined => {
  if (issueDateDocTypes.includes(documentType)) {
    const years = getValidityYearsByType(documentType);
    const minDate = new Date();
    minDate.setFullYear(minDate.getFullYear() - years);
    return minDate;
  }

  if (validDateDocTypes.includes(documentType)) {
    return new Date();
  }

  return undefined;
};

/**
 * Get the maximum date allowed for the document type
 * @returns Date | undefined - Maximum date or undefined if no restriction
 */
export const getMaxDateByDocumentType = (documentType: string): Date | undefined => {
  const today = new Date();

  if ([...issueDateDocTypes, ...completionDateDocTypes].includes(documentType)) {
    return today;
  }

  return undefined;
};

export const isDateExpired = (dateStr: string): boolean => {
  if (!dateStr) return false;

  const inputDate = new Date(dateStr);
  const today = new Date();

  // Remove time portion for accurate comparison
  inputDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  return inputDate < today;
};

export const formatListWithAnd = (items: string[]): string => {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
};

  const countryOptions = countryList().getData();


  export const getCountryName = (code: string): string => {
    const country = countryOptions.find((c) => c.value === code);
    return country?.label ?? code;
  };